// loadStripe comes from the /pure entry: the default @stripe/stripe-js entry
// injects the Stripe.js script one microtask after module evaluation (no
// loadStripe call needed) and the package carries no sideEffects flag, so in
// this single-IIFE bundle the default entry would download Stripe.js on every
// pageview. /pure defers the download to the first loadStripe call, which is
// what lets prewarmStripe control when it starts. Type-only imports stay on
// the main package — they are erased at build.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Appearance, ExpressCheckoutPaymentMethodsOption, LayoutOption, PaymentWalletsOption, Stripe } from '@stripe/stripe-js';
import { isDarkColor } from '@/theme';
import { isSandboxMode } from '@/session';
import { getSiteConfig } from '@/api/client';
import { logWarn } from '@/sdkConsole';

export async function getStripe(publishableKey: string, connectedAccount?: string): Promise<Stripe> {
  const instance = await loadStripe(publishableKey, connectedAccount ? { stripeAccount: connectedAccount } : undefined);

  if (!instance) {
    throw new Error('Failed to initialize Stripe');
  }

  return instance;
}

// prewarmStripe starts the Stripe.js download when a checkout surface opens,
// before any payment form mounts: the script load is cached module-level in
// @stripe/stripe-js (once per page, independent of key/account), so the
// mount-time getStripe — which may add a Connect stripeAccount — reuses the
// loaded script and only runs Stripe init. The key comes from the memoized
// site-config; a prewarm failure only loses the head start, so it logs and
// leaves the mount-time getStripe to surface load errors to the visitor.
export async function prewarmStripe(): Promise<void> {
  const config = await getSiteConfig();
  if (!config?.publishableKey) {
    return;
  }
  try {
    await getStripe(config.publishableKey);
  } catch (err) {
    logWarn('Stripe.js prewarm failed', err);
  }
}

// stripeAppearance is the shared look for every mounted Stripe Element — the
// paid Payment Element (Elements-with-Checkout-Sessions) and the trial Payment
// Element (SetupIntent) — so card fields render identically across both flows.
export const stripeAppearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0a2540',
    colorText: '#0f172a',
    colorTextSecondary: '#64748b',
    colorDanger: '#b42318',
    colorBackground: '#ffffff',
    colorPrimaryText: '#ffffff',
    borderRadius: '8px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

// buildStripeAppearance derives the Stripe Elements appearance from the theme
// tokens the widget has already resolved on the shadow host, so the embedded card
// form matches the surrounding paywall (Modern accent, Dark/Auto palette, and any
// publisher accent) without duplicating those values here. `el` must live inside
// the shadow tree so the `--sm-*` custom properties inherit down to it; when it is
// absent the shipped classic/light appearance is used. Stripe Elements mount in
// slotted light DOM (see useStripeElementSlot); the appearance is snapshotted
// from resolved styles because it is handed to the cross-origin iframe as
// values, not CSS variables.
export function buildStripeAppearance(el: HTMLElement | null): Appearance {
  if (!el) {
    return stripeAppearance;
  }
  const styles = getComputedStyle(el);
  const token = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;

  const surface = token('--sm-surface', '#ffffff');
  const dark = isDarkColor(surface);
  return {
    theme: dark ? 'night' : 'stripe',
    variables: {
      colorPrimary: token('--sm-accent', '#0a2540'),
      colorPrimaryText: token('--sm-on-accent', '#ffffff'),
      colorText: token('--sm-text', dark ? '#e2e8f0' : '#0f172a'),
      colorTextSecondary: token('--sm-text-secondary', dark ? '#94a3b8' : '#64748b'),
      colorDanger: token('--sm-danger', dark ? '#f87171' : '#b42318'),
      colorBackground: surface,
      borderRadius: token('--sm-radius', '8px'),
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
  };
}

// Sandbox payment forms suppress Link: Stripe recognizes the browser's real
// Link consumer session even against a test-mode intent, so a publisher
// previewing their integration would see their personal Link account
// (email/phone prefill, save-with-Link prompt) inside the card form. The
// server already pins sandbox intents card-only
// (stripeclient.CardOnlyPaymentMethodTypes), but that only removes Link as a
// payment-method tab — the inline save/recognition surface rides the card
// form and only the client-side wallets option turns it off. Live forms get
// undefined (Stripe's default auto behavior).
export function paymentElementWallets(): PaymentWalletsOption | undefined {
  if (!isSandboxMode()) {
    return undefined;
  }
  return { link: 'never' };
}

// Payment Element tab order: card first, always. With layout 'tabs' Stripe
// preselects the first tab, and absent an explicit order Stripe ranks methods
// itself — on live sessions (dynamic payment methods) that surfaced WeChat
// Pay as the preselected tab. Unlisted methods follow in Stripe's order.
export const paymentElementMethodOrder = ['card'];

// Screens mounting the Express Checkout Element as the wallet surface suppress
// the Payment Element's own wallet buttons — otherwise a wallet-capable
// browser renders Apple Pay / Google Pay twice. This also turns off the card
// form's inline Link recognition/save-with-Link surface; the ECE Link button
// is the Link surface on these screens (accepted trade-off, 2026-08-25).
export const paymentElementWalletsSuppressed: PaymentWalletsOption = {
  applePay: 'never',
  googlePay: 'never',
  link: 'never',
};

// ECE wallet visibility: 'always' renders Apple Pay / Google Pay even when the
// browser/device has no wallet card configured — the 'auto' default hides the
// button unless the visitor already has an active card in that wallet, which
// in live mode means most visitors never see the one-tap path. An unequipped
// visitor who clicks gets the wallet's setup flow. Link stays 'auto': it has
// no device prerequisite (email OTP), but 'auto' still renders it only for
// visitors Stripe recognizes as Link customers (email/phone/cookie) — verified
// 2026-08-27 against sandbox: a clean browser's ECE reports
// availablePaymentMethods undefined and renders nothing at all.
// Klarna is BNPL, not a one-tap wallet, and is unwanted on the wallet row;
// 'never' suppresses it here only — the Payment Element's tabs are unaffected.
export const expressCheckoutPaymentMethods: ExpressCheckoutPaymentMethodsOption = {
  applePay: 'always',
  googlePay: 'always',
  link: 'auto',
  klarna: 'never',
};

// Wallet buttons stack vertically on narrow viewports — the same 760px
// breakpoint styles.css uses for the single-column shell. Side-by-side
// buttons crowd a phone-width shell; maxColumns 1 forces one per row.
// maxColumns 0 is Stripe's "unlimited" default, so passing it explicitly
// keeps the desktop auto grid. overflow 'never' stops Stripe collapsing a
// wallet into the "See more" menu — every enabled button always renders
// (the 'never' + maxRows>0 combination is invalid; maxRows stays unset).
const walletStackQuery = '(max-width: 760px)';

export function walletButtonLayout(): LayoutOption {
  const stack = typeof window.matchMedia === 'function' && window.matchMedia(walletStackQuery).matches;
  return { maxColumns: stack ? 1 : 0, overflow: 'never' };
}

// onWalletStackChange fires cb when the viewport crosses the mobile
// breakpoint so a mounted ECE can re-layout via update() (rotation, window
// resize). Returns an unsubscribe. matchMedia is absent in some non-browser
// hosts; there the creation-time layout stands and the returned fn is a no-op.
export function onWalletStackChange(cb: () => void): () => void {
  if (typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const media = window.matchMedia(walletStackQuery);
  media.addEventListener('change', cb);
  return () => media.removeEventListener('change', cb);
}
